export interface OutlineItem {
  level: number;
  text: string;
  id: string;
  children: OutlineItem[];
}

/**
 * 从 Markdown 文本中提取大纲
 * @param markdown Markdown 文本
 * @returns 大纲项目数组
 */
export function extractOutlineFromMarkdown(
  markdown: string,
): OutlineItem[] {
  const lines = markdown.split('\n');
  const headers: { level: number; text: string; id: string }[] = [];

  // 用于跟踪相同标题的计数
  const headerCount: Record<string, number> = {};

  // 提取所有带数字编号的标题
  for (const line of lines) {
    const headerMatch = line.match(
      /^(#{1,6})\s+(\d+[.\d]*)\s+(.+)$/,
    );
    if (headerMatch) {
      const level = headerMatch[1].length;
      const number = headerMatch[2];
      const title = headerMatch[3].trim();
      const text = number + ' ' + title;

      // 生成唯一ID
      const baseId = text
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '');
      if (headerCount[baseId] === undefined) {
        headerCount[baseId] = 0;
      } else {
        headerCount[baseId]++;
      }
      const id =
        headerCount[baseId] > 0
          ? `${baseId}-${headerCount[baseId]}`
          : baseId;

      headers.push({ level, text, id });
    }
  }

  // 构建大纲树结构
  const root: OutlineItem[] = [];
  const stack: OutlineItem[] = [];

  for (const header of headers) {
    const item: OutlineItem = {
      level: header.level,
      text: header.text,
      id: header.id,
      children: [],
    };

    // 找到合适的父节点
    while (
      stack.length > 0 &&
      stack[stack.length - 1].level >= header.level
    ) {
      stack.pop();
    }

    if (stack.length === 0) {
      root.push(item);
    } else {
      stack[stack.length - 1].children.push(item);
    }

    stack.push(item);
  }

  return root;
}

/**
 * 将大纲转换为嵌套的 HTML 列表
 * @param outline 大纲项目数组
 * @returns HTML 字符串
 */
export function outlineToHTML(outline: OutlineItem[]): string {
  if (outline.length === 0) return '';

  let html = '<ul>';

  for (const item of outline) {
    html += `<li><a style="display: block; padding: 4px 0;" href="#${item.id}">${item.text}</a>`;
    if (item.children.length > 0) {
      html += outlineToHTML(item.children);
    }
    html += '</li>';
  }

  html += '</ul>';
  return html;
}

/**
 * 为HTML内容中的标题添加锚点ID
 * @param html HTML内容
 * @returns 添加了锚点ID的HTML内容
 */
export function addHeaderAnchors(html: string): string {
  return html.replace(
    /<h([1-6])>(\d+[.\d]*\s+.+?)<\/h\1>/g,
    (_, level, content) => {
      const text = content.trim();
      const id = text
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '');
      return `<h${level} id="${id}">${content}</h${level}>`;
    },
  );
}
